import {
  filterLibrary,
  libraryItemCategories,
  libraryPreviewUrl,
  type ImageLibrary,
  type LibraryItem,
} from "../native/image-library";
import "./native-image-controls.css";
import { LibraryTap } from "./native-library-tap";
import { imageMediaType } from "../native/image-formats";
const base = "/templates/boxed/controllers/creatorenginepro/assets/cliparts/";
const el = <T extends HTMLElement>(id: string) =>
  document.getElementById(id) as T;
/** The original library UI; all insertion is delegated to the native image importer. */
export class NativeImageLibrary {
  private data?: ImageLibrary;
  private category = "all";
  private query = "";
  private selected: LibraryItem | undefined;
  private hovered: LibraryItem | undefined;
  private focused: LibraryItem | undefined;
  private generation = 0;
  private loading = false;
  private backdrop = false;
  private overlay = el("clipart-picker-overlay");
  private taps = new LibraryTap(el("clipart-grid"));
  constructor(
    private insert: (
      file: File,
      name?: string,
      current?: () => boolean,
    ) => Promise<void>,
    private prepare: () => boolean,
    private message: (text: string, error?: boolean) => void,
  ) {
    el("native-image").onclick = () => void this.open();
    for (const id of ["clipart-picker-close", "clipart-picker-cancel"])
      el(id).onclick = () => this.close();
    el("clipart-picker-use").onclick = () => void this.use();
    el<HTMLInputElement>("clipart-search").oninput = (e) => {
      this.query = (e.target as HTMLInputElement).value;
      this.selected = undefined;
      this.render();
    };
    el<HTMLSelectElement>("clipart-category-select").onchange = (e) => {
      this.category = (e.target as HTMLSelectElement).value;
      this.selected = undefined;
      this.render();
    };
    el("clipart-search-toggle").onclick = () => {
      const open = this.overlay
        .querySelector(".clipart-picker")!
        .classList.toggle("mobile-search-open");
      el("clipart-search-toggle").ariaLabel = open
        ? "Закрыть поиск"
        : "Открыть поиск";
      if (open) el("clipart-search").focus();
    };
    this.overlay.onpointerdown = (e) => {
      if (!(e.target as Element).closest("#clipart-grid")) this.taps.reset();
      this.backdrop = e.target === this.overlay;
    };
    this.overlay.onclick = (e) => {
      if (this.backdrop && e.target === this.overlay) this.close();
      this.backdrop = false;
    };
    window.addEventListener(
      "keydown",
      (e) => {
        if (!this.opened) return;
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.close();
        }
        if (e.key === "Tab") {
          const controls = Array.from(
            this.overlay.querySelectorAll<HTMLElement>(
              "button:not(:disabled),input,select",
            ),
          ).filter((n) => n.getClientRects().length);
          const index = controls.indexOf(document.activeElement as HTMLElement);
          if (
            (e.shiftKey && index <= 0) ||
            (!e.shiftKey && index === controls.length - 1)
          ) {
            e.preventDefault();
            controls[e.shiftKey ? controls.length - 1 : 0]?.focus();
          }
        }
      },
      true,
    );
  }
  get opened() {
    return this.overlay.classList.contains("open");
  }
  async open() {
    if (!this.prepare()) return;
    const token = ++this.generation;
    this.overlay.classList.add("open");
    this.overlay.setAttribute("aria-hidden", "false");
    this.render();
    try {
      const response = await fetch(base + "manifest.txt?v=" + Date.now(), {
        cache: "no-store",
      });
      if (!response.ok)
        throw new Error(
          "Не удалось загрузить библиотеку. Можно загрузить изображение с компьютера.",
        );
      const data = (await response.json()) as ImageLibrary;
      if (!Array.isArray(data.categories) || !Array.isArray(data.items))
        throw new Error("Не удалось прочитать каталог изображений.");
      if (token !== this.generation) return;
      this.data = {
        categories: data.categories,
        items: data.items.filter(
          (item) =>
            typeof item.file === "string" &&
            !item.file.includes("..") &&
            /^[\w./-]+\.(png|jpe?g|webp|svg)$/i.test(item.file),
        ),
      };
      this.render();
      if (innerWidth > 700) el("clipart-search").focus();
    } catch (error) {
      if (token === this.generation) {
        el("clipart-grid").append(
          Object.assign(document.createElement("p"), {
            textContent: (error as Error).message,
          }),
        );
        this.message((error as Error).message, true);
      }
    }
  }
  close() {
    this.taps.reset();
    this.generation++;
    this.loading = false;
    this.overlay.classList.remove("open");
    this.overlay.setAttribute("aria-hidden", "true");
    this.overlay
      .querySelector(".clipart-picker")!
      .classList.remove("mobile-search-open");
    this.selected = undefined;
    this.hovered = this.focused = undefined;
    this.highlightCategories();
    el("native-overlay").focus();
  }
  async upload(file: File, name?: string) {
    if (this.loading) return;
    this.loading = true;
    const token = ++this.generation;
    this.render();
    try {
      await this.insert(file, name, () => token === this.generation);
      if (token === this.generation) this.close();
    } catch (error) {
      if (token === this.generation)
        this.message((error as Error).message, true);
    } finally {
      if (token === this.generation) {
        this.loading = false;
        this.render();
      }
    }
  }
  private async use() {
    if (!this.selected || this.loading) return;
    const item = this.selected,
      token = ++this.generation;
    this.loading = true;
    this.render();
    try {
      const response = await fetch(base + item.file, { cache: "force-cache" });
      if (!response.ok)
        throw new Error("Не удалось загрузить изображение. Повторите попытку.");
      const blob = await response.blob();
      if (token !== this.generation) return;
      await this.insert(
        new File([blob], item.file, { type: blob.type }),
        item.name || item.title,
        () => token === this.generation,
      );
      if (token === this.generation) this.close();
    } catch (error) {
      if (token === this.generation)
        this.message((error as Error).message, true);
    } finally {
      if (token === this.generation) {
        this.loading = false;
        this.render();
      }
    }
  }
  private highlightCategories() {
    const item = this.hovered ?? this.focused ?? this.selected;
    const ids = item ? libraryItemCategories(item) : [];
    const categories = (this.data?.categories ?? []).filter((c) =>
      ids.includes(c.id),
    );
    for (const button of el("clipart-filter-row").querySelectorAll<HTMLElement>(
      "[data-category]",
    ))
      button.classList.toggle(
        "image-category",
        ids.includes(button.dataset.category!),
      );
    const hint = el("clipart-category-hint");
    const text = categories.length
      ? `${categories.length === 1 ? "Раздел картинки" : "Разделы картинки"}: ${categories.map((c) => c.title).join(", ")}`
      : "";
    if (hint.textContent !== text) hint.textContent = text;
  }
  private render() {
    this.taps.reset();
    this.hovered = this.focused = undefined;
    const filters = el("clipart-filter-row"),
      select = el<HTMLSelectElement>("clipart-category-select"),
      grid = el("clipart-grid");
    filters.replaceChildren();
    select.replaceChildren();
    for (const c of this.data?.categories ?? []) {
      const button = Object.assign(document.createElement("button"), {
        type: "button",
        textContent: c.title,
        className: "clipart-filter" + (c.id === this.category ? " active" : ""),
      });
      button.dataset.category = c.id;
      button.ariaPressed = String(c.id === this.category);
      button.onclick = () => {
        this.category = c.id;
        this.selected = undefined;
        this.render();
      };
      filters.append(button);
      select.add(new Option(c.title, c.id, false, c.id === this.category));
    }
    grid.replaceChildren();
    const upload = Object.assign(document.createElement("button"), {
      id: "clipart-upload-tile",
      type: "button",
      className: "clipart-tile clipart-upload-tile",
      disabled: this.loading,
    });
    upload.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 16h2V8l3.5 3.5 1.4-1.4L12 4.2 6.1 10.1l1.4 1.4L11 8v8zm-6 2h14v2H5v-2z"/></svg><span>Загрузить<br>с компьютера</span>';
    upload.onclick = () => el<HTMLInputElement>("native-image-file").click();
    upload.ondragover = (e) => {
      e.preventDefault();
      upload.classList.add("drag-over");
    };
    upload.ondragleave = () => upload.classList.remove("drag-over");
    upload.ondrop = (e) => {
      e.preventDefault();
      upload.classList.remove("drag-over");
      const file = Array.from(e.dataTransfer?.files ?? []).find((f) =>
        imageMediaType(f.type, f.name),
      );
      if (file) void this.upload(file);
    };
    grid.append(upload);
    const items = this.data
      ? filterLibrary(this.data, this.category, this.query)
      : [];
    for (const item of items) {
      const tile = Object.assign(document.createElement("button"), {
        type: "button",
        className:
          "clipart-tile" +
          (this.selected?.file === item.file ? " selected" : ""),
        disabled: this.loading,
      });
      tile.dataset.file = item.file;
      tile.ariaPressed = String(this.selected?.file === item.file);
      const memberships = libraryItemCategories(item);
      const categories = (this.data?.categories ?? [])
        .filter((c) => memberships.includes(c.id))
        .map((c) => c.title);
      if (categories.length)
        tile.setAttribute(
          "aria-description",
          "Разделы: " + categories.join(", "),
        );
      tile.onpointerenter = (event) => {
        if (event.pointerType === "touch" || this.loading) return;
        this.hovered = item;
        this.highlightCategories();
      };
      tile.onpointerleave = () => {
        if (this.hovered === item) this.hovered = undefined;
        this.highlightCategories();
      };
      tile.onfocus = () => {
        if (tile.matches(":focus-visible")) this.focused = item;
        this.highlightCategories();
      };
      tile.onblur = () => {
        if (this.focused === item) this.focused = undefined;
        this.highlightCategories();
      };
      tile.append(
        Object.assign(document.createElement("img"), {
          loading: "lazy",
          decoding: "async",
          src: libraryPreviewUrl(item, base),
          alt: item.name || item.title || item.file,
        }),
        Object.assign(document.createElement("span"), {
          className: "clipart-tile-name",
          textContent: item.name || item.title || item.file,
        }),
      );
      // Keep the tile alive for both native mouse double-clicks and touch taps.
      tile.onclick = (event) => {
        if (!this.opened || this.loading) return;
        const tap = this.taps.click(tile, event);
        if (tap === "ignore") return;
        this.selected = item;
        grid.querySelectorAll<HTMLElement>(".selected").forEach((n) => {
          n.classList.remove("selected");
          n.ariaPressed = "false";
        });
        tile.classList.add("selected");
        tile.ariaPressed = "true";
        this.highlightCategories();
        el<HTMLButtonElement>("clipart-picker-use").disabled = false;
        if (tap === "double") void this.use();
      };
      tile.ondblclick = () => {
        // Touch activation belongs to LibraryTap, never a compatibility dblclick.
        if (this.opened && !this.loading && !this.taps.fromTouch)
          void this.use();
      };
      grid.append(tile);
    }
    if (!items.length)
      grid.append(
        Object.assign(document.createElement("div"), {
          className: "clipart-empty",
          textContent: !this.data
            ? "Загрузка библиотеки…"
            : this.query
              ? "По этому запросу ничего не найдено."
              : "В этой категории пока нет изображений.",
        }),
      );
    el<HTMLButtonElement>("clipart-picker-use").disabled =
      !this.selected || this.loading;
    this.highlightCategories();
  }
}
